import React, { useState, useEffect, FC } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { supabase } from '../../../../config/supabaseClient';
import { FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';
import toast from 'react-hot-toast';

/**
 * Type definition for social media platform connections
 */
export interface SocialAccount {
  id: string;
  platform: 'facebook' | 'twitter' | 'linkedin' | 'instagram';
  name: string;
  connected: boolean;
  profilePicture?: string;
  accessToken?: string;
  tokenExpiry?: string;
  refreshToken?: string;
  userId: string;
}

/**
 * Social Account Connector Component
 * Manages connections to social media platforms and handles OAuth flows
 */
const SocialAccountConnector: React.FC = () => {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();
  const userId = user?.id;

  // Platforms configuration
  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <FaFacebook className="text-blue-600" size={20} />,
      color: 'bg-blue-100 hover:bg-blue-200 border-blue-300',
      textColor: 'text-blue-700'
    },
    {
      id: 'twitter',
      name: 'Twitter',
      icon: <FaTwitter className="text-blue-400" size={20} />,
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      textColor: 'text-blue-500'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: <FaLinkedin className="text-blue-700" size={20} />,
      color: 'bg-blue-100 hover:bg-blue-200 border-blue-300',
      textColor: 'text-blue-700'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <span className="font-bold text-purple-600">Ig</span>,
      color: 'bg-purple-100 hover:bg-purple-200 border-purple-300',
      textColor: 'text-purple-700'
    }
  ];

  // Initialize and fetch connected accounts when component mounts
  useEffect(() => {
    if (userId) {
      fetchConnectedAccounts();
      
      // Check for redirect from OAuth flow
      const urlParams = new URLSearchParams(window.location.search);
      const connectionStatus = urlParams.get('connection');
      
      if (connectionStatus === 'success') {
        setSuccess('Account connected successfully!');
        toast.success('Account connected successfully!');
        // Clear the URL parameter after handling it
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (connectionStatus === 'failed') {
        const error = urlParams.get('error');
        setError(`Connection failed: ${error || 'Unknown error'}`);
        toast.error(`Connection failed: ${error || 'Unknown error'}`);
        // Clear the URL parameter after handling it
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [userId]);

  /**
   * Connect to a social media platform
   */
  const connectAccount = (platform: string) => {
    setError('');
    if (!userId) {
      setError('You must be logged in to connect accounts.');
      return;
    }
    
    // Get OAuth redirect URL based on platform
    let authUrl = '';
    // Use backend URL for redirect URI (separate from frontend)
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000'; 
    const redirectUri = `${backendUrl}/api/auth/${platform}/callback`;
    
    switch(platform) {
      case 'facebook':
        // Facebook OAuth 2.0 flow
        const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'your-facebook-app-id';
        authUrl = `https://www.facebook.com/v16.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${userId}&scope=pages_manage_posts,pages_read_engagement,public_profile`;
        break;
        
      case 'twitter':
        // Twitter OAuth 2.0 flow
        const twitterClientId = process.env.REACT_APP_TWITTER_CLIENT_ID || 'your-twitter-client-id';
        authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${twitterClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=tweet.read%20tweet.write%20users.read&state=${userId}&code_challenge=challenge&code_challenge_method=plain`;
        break;
        
      case 'linkedin':
        // LinkedIn OAuth 2.0 flow
        const linkedinClientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '';
        const isProd = process.env.NODE_ENV === 'production';
        const linkedinRedirectUri = isProd
          ? 'https://api.explanatum.co.uk/api/auth/linkedin/callback'
          : 'http://localhost:5000/api/auth/linkedin/callback';
        
        // Build the LinkedIn auth URL - important to use consistent encoding
        authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${linkedinClientId}&redirect_uri=${encodeURIComponent(linkedinRedirectUri)}&state=${userId}&scope=openid%20profile%20email`;
        
        console.log('LinkedIn redirect URI:', linkedinRedirectUri);
        console.log('Full LinkedIn auth URL:', authUrl);
        break;
        
      case 'instagram':
        // Instagram (via Facebook) OAuth flow
        const igAppId = process.env.REACT_APP_INSTAGRAM_APP_ID || 'your-instagram-app-id';
        authUrl = `https://api.instagram.com/oauth/authorize?client_id=${igAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user_profile,user_media&response_type=code&state=${userId}`;
        break;
        
      default:
        setError(`Unsupported platform: ${platform}`);
        return;
    }
    
    // Instead of using a popup, directly navigate to the auth URL
    // This helps with LinkedIn's security restrictions on popups
    if (platform === 'linkedin') {
      console.log('Redirecting to LinkedIn auth page directly...');
      toast('Redirecting to LinkedIn login...', { icon: 'ℹ️' });
      // Store redirect info in sessionStorage
      sessionStorage.setItem('oauth_platform', platform);
      sessionStorage.setItem('oauth_return_path', window.location.pathname + '?view=accounts');
      // Direct browser redirect instead of popup
      window.location.href = authUrl;
      return;
    }
    
    // Open OAuth popup for other platforms
    const width = 600;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;
    
    const popup = window.open(
      authUrl, 
      `Connect ${platform}`,
      `width=${width},height=${height},left=${left},top=${top}`
    );
    
    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      setError('Popup was blocked. Please allow popups for this site.');
      return;
    }
    
    // Poll to check when popup is closed
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        // Fetch accounts again to see if connection was successful
        fetchConnectedAccounts();
      }
    }, 500);
    
    // Set a timeout to clear the interval if the popup stays open too long
    setTimeout(() => {
      clearInterval(timer);
      setLoading(false);
    }, 120000); // 2 minutes timeout
  };

  // Fetch accounts data from Supabase
  const fetchConnectedAccounts = async () => {
    if (!userId) {
      setError('User must be logged in to view connected accounts');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Query the social_accounts table
      const { data, error } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('user_id', userId);
        
      if (error) throw error;
      
      if (data) {
        // Map DB results to our SocialAccount interface
        const formattedAccounts: SocialAccount[] = data.map((acc: any) => ({
          id: acc.id,
          platform: acc.platform,
          name: acc.account_name || acc.platform,
          connected: true,
          profilePicture: acc.profile_picture || undefined,
          accessToken: acc.access_token,
          tokenExpiry: acc.token_expiry,
          refreshToken: acc.refresh_token,
          userId: acc.user_id
        }));
        
        setAccounts(formattedAccounts);
      }
    } catch (err: any) {
      setError(`Error fetching connected accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Disconnect an already connected social media account
   */
  const disconnectAccount = async (accountId: string, platform: string) => {
    if (!userId || !accountId) return;
    
    try {
      const { error } = await supabase
        .from('social_accounts')
        .delete()
        .eq('id', accountId)
        .eq('user_id', userId);
        
      if (error) throw error;
      
      // Update local state
      setAccounts(accounts.filter(acc => acc.id !== accountId));
      setSuccess(`Successfully disconnected your ${platform} account`);
    } catch (err: any) {
      setError(`Error disconnecting account: ${err.message}`);
    }
  };

  // Helper function for Twitter's PKCE flow
  const generateCodeChallenge = () => {
    // In a real implementation, this would be a proper SHA-256 challenge
    // For now, return a placeholder
    return 'twitter_code_challenge_placeholder';
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Connected Accounts</h2>
        <p className="text-gray-600">Connect your social media accounts to automatically post and schedule content</p>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 relative" role="alert">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)} 
            className="absolute top-0 right-0 p-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 relative" role="alert">
          <p>{success}</p>
          <button 
            onClick={() => setSuccess(null)} 
            className="absolute top-0 right-0 p-2 text-green-500 hover:text-green-700"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {platforms.map(platform => {
          // Find if this platform is already connected
          const connectedAccount = accounts.find(acc => acc.platform === platform.id);
          const isConnected = !!connectedAccount;
          
          return (
            <div 
              key={platform.id} 
              className={`border rounded-lg p-4 ${platform.color}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    {platform.icon}
                  </div>
                  <div>
                    <h3 className={`font-medium ${platform.textColor}`}>{platform.name}</h3>
                    <p className="text-sm text-gray-600">
                      {isConnected 
                        ? `Connected as ${connectedAccount?.name}` 
                        : 'Not connected'}
                    </p>
                  </div>
                </div>
                
                <div>
                  {isConnected ? (
                    <button 
                      onClick={() => disconnectAccount(connectedAccount?.id!, platform.id)}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button 
                      onClick={() => connectAccount(platform.id)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
              
              {isConnected && (
                <div className="mt-4 text-sm text-gray-600">
                  <p>
                    {connectedAccount?.tokenExpiry && (
                      <>
                        <span className="font-medium">Token expires: </span>
                        {new Date(connectedAccount.tokenExpiry).toLocaleString()}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="mt-8 bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2">Why connect your accounts?</h3>
        <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
          <li>Schedule posts across multiple platforms from one dashboard</li>
          <li>Auto-publish content at optimal times for engagement</li>
          <li>Track performance metrics and analytics in one place</li>
          <li>No need to manually copy-paste between platforms</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialAccountConnector;
